import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  Chip,
  Paper,
} from '@mui/material';
import { Link } from 'react-router-dom';
import RecommendationCard from './RecommendationCard';
import RecommendationDetail from './RecommendationDetail';
import ShowReviewDialog from './ShowReviewDialog';
import useReviewHandler from "../hooks/useReviewHandler"; 
import Loader from "../Loader";
import SentimentDissatisfiedRoundedIcon from '@mui/icons-material/SentimentDissatisfiedRounded';
import { Zoom } from '@mui/material';

const genreMaps = {
  movie: new Map([
    [28, "Acción"], [12, "Aventura"], [16, "Animación"], [35, "Comedia"], [80, "Crimen"], [99, "Documental"],
    [18, "Drama"], [10751, "Familiar"], [14, "Fantasía"], [36, "Historia"], [27, "Terror"], [10402, "Música"],
    [9648, "Misterio"], [10749, "Romance"], [878, "Ciencia Ficción"], [10770, "Película de TV"], [53, "Suspenso"],
    [10752, "Bélica"], [37, "Western"]
  ]),
  tv: new Map([
    [10759, "Acción y Aventura"], [16, "Animación"], [35, "Comedia"], [80, "Crimen"], [99, "Documental"], [18, "Drama"],
    [10751, "Familiar"], [10762, "Infantil"], [9648, "Misterio"], [10763, "Noticias"], [10764, "Reality"],
    [10765, "Ciencia Ficción y Fantasía"], [10766, "Novela"], [10767, "Talk Show"], [10768, "Guerra y Política"], [37, "Western"]
  ])
};

const Recommendations = ({ idToken }) => {
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedType, setSelectedType] = useState("movie");
  const [recommendationDetailOpen, setRecommendationDetailOpen] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [showDialogRecommendation, setShowReviewDialogOpen] = useState(false);
  const [currentShowId, setCurrentShowId] = useState(null);
  const [reviewLoading, handleReview] = useReviewHandler(idToken);
  const [hasSearched, setHasSearched] = useState(false); 
  const resultsContainerRef = useRef(null);

  useEffect(() => {
    if (hasSearched && resultsContainerRef.current) {
        resultsContainerRef.current.scrollIntoView({ behavior: 'smooth' });
        setHasSearched(false);
    }
  }, [hasSearched]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://script.google.com/macros/s/AKfycbxREPDO9_xArVTVk3trXhFg-u76nPzCES2MoIyBtsUPy7xTGYoNRClNu-AMkltdCSZx/exec?action=getrecommendations&id_token=${idToken}&genres=${selectedGenres.join(',')}&type=${selectedType}`);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      if (data.status !== 'success') throw new Error('Api error');
      setRecommendations(data.content.sort((a, b) => b.score - a.score));
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setRecommendations([]);
    } finally {
      setHasSearched(true);
      setLoading(false);
    }
  };

  const handleGenreClick = (genre) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre]
    );
  };

  const handleTypeClick = (type) => {
    setSelectedType(type);
    setSelectedGenres([]);
  };

  const openRecommendationDetail = (rec) => {
    setSelectedRecommendation(rec);
    setRecommendationDetailOpen(true);
  };

  const closeModal = () => setRecommendationDetailOpen(false);

  const openShowReviewDialog = (uniqueId) => {
    setCurrentShowId(uniqueId);
    setShowReviewDialogOpen(true);
  };

  const closeShowReviewDialog = () => setShowReviewDialogOpen(false);

  const doHandleReview = async (recommendation) => {
    const reviewHandled = await handleReview(recommendation, currentShowId);
    if (reviewHandled) {
      setRecommendations((prev) =>
        prev.filter((rec) => rec.show.uniqueId !== currentShowId)
      );
      closeShowReviewDialog();
    } else {
      alert('Hubo un error al enviar la reseña');
    }
  };

  const genres = Array.from(genreMaps[selectedType]?.values() || []);

  return (
    <Container maxWidth="md" sx={{ padding: 2 }}>
      <Box my={4}>
        <Typography variant="h5" gutterBottom align='center'>
          ¿Qué te gustaría ver hoy?
        </Typography>
      </Box>

      <Box my={2}>
        <Typography variant="h6" gutterBottom align='center'>
          ¿Película o Serie?
        </Typography>
        <Box display="flex" justifyContent="center" mt={2}>
          {['movie', 'tv'].map((type) => (
            <Chip
              key={type}
              label={type === 'movie' ? "Película" : "Serie"}
              clickable
              disabled={loading}
              color={selectedType === type ? 'primary' : 'default'}
              onClick={() => handleTypeClick(type)}
              style={{ margin: '0 8px' }}
            />
          ))}
        </Box>
      </Box>

      <Box my={2}>
        <Typography variant="h6" gutterBottom align='center'>
          ¿Algún género en particular?
        </Typography>
        <Box display="flex" justifyContent="center" flexWrap="wrap" mt={2}>
          {genres.map((genre) => (
            <Chip
              key={genre}
              label={genre}
              disabled={loading}
              clickable
              color={selectedGenres.includes(genre) ? 'primary' : 'default'}
              onClick={() => handleGenreClick(genre)}
              style={{ margin: '4px' }}
            />
          ))}
        </Box>
      </Box>

      <Grid container spacing={2} alignItems="center" style={{ marginTop: '16px' }}>
        <Grid item xs={12}>
          <Button
            disabled={loading}
            variant="contained"
            color="primary"
            onClick={fetchRecommendations}
            fullWidth
            style={{ borderRadius: '20px', padding: '10px 20px', fontSize: '1rem' }}
          >
            Buscar
          </Button>
        </Grid>
      </Grid>

      <Box mt={3}>
        {loading ? (
          <Loader />
        ) : (
          <div ref={resultsContainerRef}>
            {recommendations?.length ? (
                <Grid container spacing={2} style={{ marginTop: '16px' }}>
                  {recommendations.map((rec, index) => (
                    <Zoom key={rec.show.uniqueId} in={true} timeout={index * 10 + 400}>
                      <Grid item xs={6} sm={4} md={4}>
                        <RecommendationCard
                          rec={rec}
                          onCardClick={openRecommendationDetail}
                          onReviewButtonClick={openShowReviewDialog}
                        />
                      </Grid>
                    </Zoom>
                  ))}
                </Grid>
            ) : (
              recommendations !== null && (
                  <Grid container justifyContent="center" alignItems="center" >
                    <Paper elevation={0} style={{ padding: '20px', textAlign: 'center' }}>
                      <Box
                        component="img"
                        src="/empty-state.png"
                        sx={{
                          width: 128,
                          height: 128,
                          position: 'relative'
                        }}
                      />
                      <Typography variant="body1" style={{ marginTop: '16px' }}>
                        Por ahora no hay recomendaciones disponibles.
                      </Typography>
                      <Typography variant="body2" style={{ marginTop: '8px' }}>
                        ¡Invita más amigos a tu <b><Link to="/circle"  style={{ color:"black"}}>círculo</Link></b> para descubrir nuevos contenidos!
                      </Typography>
                    </Paper>
                  </Grid>
              )
            )}
          </div>
        )}
      </Box>

      <RecommendationDetail
        open={recommendationDetailOpen}
        onClose={closeModal}
        recommendation={selectedRecommendation}
      />
      <ShowReviewDialog
        open={showDialogRecommendation}
        onClose={closeShowReviewDialog}
        reviewLoading={reviewLoading}
        handleReview={doHandleReview}
      />
    </Container>
  );
};

export default Recommendations;
